
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatEscalationInterface, EscalationRepositoryInterface, TicketEscalationInterface,
} from '@/services/QC-V2/escalation/interfaces';
import { ChatInterface, FilterRequestData, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import EscalationsFilters from '@/components/qa/escalation/EscalationsFilters.vue';
import { TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import EscalationChatsTable from '@/components/qa/escalation/EscalationChatsTable.vue';
import EscalationTicketsTable from '@/components/qa/escalation/EscalationTicketsTable.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'escalationRepositoryV2',
    'profileRepository',
  ],
  components: {
    EscalationsFilters,
    EscalationChatsTable,
    EscalationTicketsTable,
  },
})
export default class AllEscalations extends Vue {
  private escalationRepositoryV2!: EscalationRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private chatEscalations: ChatEscalationInterface[] = [];
  private totalChatEscalations: number = 0;
  private chats: ChatInterface[] = [];
  private disabledChatIds: string[] = [];
  private chatsPage: number = 1;

  private filter: FilterRequestData = { page: 1 };

  private ticketEscalations: TicketEscalationInterface[] = [];
  private totalTicketEscalations: number = 0;
  private tickets: TicketInterface[] = [];
  private disabledTicketsIds: number[] = [];
  private ticketsPage: number = 1;

  private profilesMap: ProfileMapInterface = {};

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      const [
        [chatEscalations, totalChatEscalations],
        [ticketEscalations, totalTicketEscalations],
      ] = await Promise.all([
        this.initializeChatEscalations(),
        this.initializeTicketEscalations(),
      ]);

      await this.fetchProfiles(chatEscalations, ticketEscalations);

      this.chatEscalations = chatEscalations;
      this.totalChatEscalations = totalChatEscalations;
      this.chats = this.chatEscalations.map((escalation) => escalation.chat);

      this.ticketEscalations = ticketEscalations;
      this.totalTicketEscalations = totalTicketEscalations;
      this.tickets = this.ticketEscalations.map((escalation) => escalation.ticket);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initializeChatEscalations(): Promise<[ChatEscalationInterface[], number]> {
    return this.escalationRepositoryV2.findAllChatEscalations({
      ...this.filter,
      page: this.chatsPage,
    });
  }

  private async initializeTicketEscalations(): Promise<[TicketEscalationInterface[], number]> {
    return this.escalationRepositoryV2.findAllTicketEscalations({
      ...this.filter,
      page: this.ticketsPage,
    });
  }

  private async fetchProfiles(
    chatEscalations: ChatEscalationInterface[],
    ticketEscalations: TicketEscalationInterface[],
  ): Promise<void> {
    const ids: string[] = [];
    chatEscalations.forEach((escalation) => {
      if (!ids.includes(escalation.escalatedFrom)) {
        ids.push(escalation.escalatedFrom);
      }

      if (!!escalation.departmentManager && !ids.includes(escalation.departmentManager)) {
        ids.push(escalation.departmentManager);
      }

      if (!!escalation.shiftManager && !ids.includes(escalation.shiftManager)) {
        ids.push(escalation.shiftManager);
      }
    });

    ticketEscalations.forEach((escalation) => {
      if (!ids.includes(escalation.escalatedFrom)) {
        ids.push(escalation.escalatedFrom);
      }

      if (!!escalation.departmentManager && !ids.includes(escalation.departmentManager)) {
        ids.push(escalation.departmentManager);
      }

      if (!!escalation.shiftManager && !ids.includes(escalation.shiftManager)) {
        ids.push(escalation.shiftManager);
      }
    });


    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private async filterEscalations(filter: FilterRequestData): Promise<void> {
    this.ticketsPage = 1;
    this.chatsPage = 1;
    this.filter = filter;
    await this.initialize();
  }

  private async getChatEscalation(escalationId: number): Promise<void> {
    const route = this.$router.resolve({ name: 'chat_escalation', params: { id: String(escalationId) } });
    window.open(route.href, '_blank');
  }

  private async changeChatsPage(page: number): Promise<void> {
    if (this.chatsPage === page) {
      return;
    }

    this.chatsPage = page;

    try {
      const [chatEscalations, totalChatEscalations] = await this.initializeChatEscalations();

      await this.fetchProfiles(chatEscalations, []);

      this.chatEscalations = chatEscalations;
      this.totalChatEscalations = totalChatEscalations;
      this.chats = this.chatEscalations.map((escalation) => escalation.chat);

    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getTicketEscalation(escalationId: number): Promise<void> {
    const route = this.$router.resolve({ name: 'ticket_escalation', params: { id: String(escalationId) } });
    window.open(route.href, '_blank');
  }

  private async changeTicketsPage(page: number): Promise<void> {
    if (this.ticketsPage === page) {
      return;
    }

    this.ticketsPage = page;

    try {
      const [ticketEscalations, totalTicketEscalations] = await this.initializeTicketEscalations();

      await this.fetchProfiles([], ticketEscalations);

      this.ticketEscalations = ticketEscalations;
      this.totalTicketEscalations = totalTicketEscalations;
      this.tickets = this.ticketEscalations.map((escalation) => escalation.ticket);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }
}
